import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import mapboxgl, { LngLat, Map, Popup, MapMouseEvent } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { AddMarkerForm } from 'components/addMarkerForm'
import { PopupContent } from 'components/popup'
import { Marker } from 'types/markers'
import { Feature } from 'types/features'
import { adminUserConnected, getAdminDisplayName } from 'lib/adminHelper'
import { adminValidateMarker, adminDeleteSuggestedMarker } from 'lib/model'
import { ActivationButton } from 'components/activationButton'
import fr from 'locales/fr.yaml'
import { useHistory } from 'react-router-dom'

import './Map.scss'
import filter1PNG from './custom-icons/filter1.png'
import sunbathPNG from './custom-icons/filter2.png'
import grillPNG from './custom-icons/filter3.png'
import picknickPNG from './custom-icons/filter4.png'
import addPNG from './custom-icons/add-marker.png'
import glassIcon from './custom-icons/glass-icon.png'

const localisationStrings = { fr }
const isMobile = window.innerWidth < 768

mapboxgl.accessToken =
  'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJja292ZWk5ZnIwN2w0MndwZWQ0Nmpqcm1iIn0.d1OaviBwQQGO1_roYeaimQ'

const publisherDomain = parseHostname().publicationDomain
const center =
  publisherDomain === '24heures.ch'
    ? new LngLat(6.627086027591877, 46.52656029698641) // Vaud -- au depart (6.479, 46.39)
    : new LngLat(6.149148865233963, 46.22096165636209) // Genève
const zoom = 9
const filterNames = ['filter1', 'filter2', 'filter3', 'filter4']
const debug = false

export const MapContext = React.createContext<mapboxgl.Map | null>(null)

type MapProps = {
  locale: 'fr'
  markers: Marker[]
  showAdminView: boolean
  userId: string
  articleId: number
  panTo: LngLat | undefined
  handleUpdateMarkers: (markerId: string, updateType: string) => void
  handleMapLoaded: (map) => void
}

export class CollaborativeMap extends Component<
  MapProps,
  {
    newMarkerFormVisible: boolean
    editMarker: boolean
    markerData: Marker | undefined
    hiddenMarkers: string[]
    needsMarkers: boolean
    mapEditing: boolean
    newMarkerPos: LngLat | undefined
    filter1On: boolean
    filter2On: boolean
    filter3On: boolean
    filter4On: boolean
  }
> {
  mapContainer: HTMLDivElement | undefined
  map: Map | undefined
  popup: Popup | undefined
  needsMarkerOnMapLoad = false
  formVisible = false
  draggableMarkers = []

  constructor(props: MapProps) {
    super(props)
    this.state = {
      newMarkerFormVisible: false,
      editMarker: false,
      markerData: undefined,
      hiddenMarkers: [],
      newMarkerPos: undefined,
      needsMarkers: false,
      mapEditing: true,
      filter1On: true,
      filter2On: true,
      filter3On: true,
      filter4On: true
    }
  }

  validateRequest = (markerId: string) => {
    console.log('Validating', markerId)
    // Check locally if admin is connected
    if (adminUserConnected()) {
      const adminUsername = getAdminDisplayName()
      console.log('Admin user connected:', adminUsername)

      // Server-side check for admin
      adminValidateMarker(markerId, adminUsername)
    }

    // needsReview = false
    this.props.handleUpdateMarkers(markerId, 'reviewed')
    // Also remove it from the map
    this.hideMarker(markerId)
  }

  editRequest = (markerData: Marker) => {
    console.log('Edit request for', markerData.markerId)
    if (this.popup) {
      this.popup.remove()
    }
    if (markerData.latitude && markerData.longitude) {
      this.setState({
        newMarkerFormVisible: true,
        editMarker: true,
        markerData: markerData,
        newMarkerPos: new LngLat(markerData.longitude, markerData.latitude)
      })
      this.createDraggableMarker(markerData.longitude, markerData.latitude)
      this.hideMarker(markerData.markerId)
    }
  }

  suggestChanges = (markerData: Marker) => {
    console.log('Suggesting changes for', markerData.markerId)
    if (this.popup) {
      this.popup.remove()
    }
    if (markerData.latitude && markerData.longitude) {
      this.setState({
        newMarkerFormVisible: true,
        editMarker: true,
        markerData: markerData,
        newMarkerPos: new LngLat(markerData.longitude, markerData.latitude)
      })
      this.createDraggableMarker(markerData.longitude, markerData.latitude)
      this.hideMarker(markerData.markerId)
    }
  }

  deleteRequest = (markerId: string) => {
    console.log('Deleting', markerId)
    adminDeleteSuggestedMarker(markerId)

    // Remove from suggested marker list
    this.props.handleUpdateMarkers(markerId, 'reviewed')
    // Also remove it from the map
    this.hideMarker(markerId)
  }

  closeForm = () => {
    if (this.state.markerData) {
      this.unhideMarker(this.state.markerData.markerId)
    }
    this.formVisible = false
    this.setState({
      newMarkerFormVisible: false,
      editMarker: false,
      markerData: undefined,
      newMarkerPos: undefined
    })

    this.draggableMarkers = []
    if (this.map) {
      this.map.getSource('draggableMarkers').setData({
        type: 'FeatureCollection',
        features: this.draggableMarkers
      })
    }
  }

  createDraggableMarker(lng: number, lat: number) {
    console.log('create marker')
    const draggableCircle = {
      geometry: {
        type: 'Point',
        coordinates: [lng, lat]
      }
    }
    this.draggableMarkers.push(draggableCircle)

    if (this.map) {
      const draggableMarkers = this.map.getSource('draggableMarkers')
      if (draggableMarkers) {
        draggableMarkers.setData({
          type: 'FeatureCollection',
          features: [draggableCircle]
        })
      }
    }
  }

  hideMarker(markerId: string) {
    this.setState({ hiddenMarkers: [markerId] })
  }

  unhideMarker(markerId: string) {
    this.setState({ hiddenMarkers: [] })
  }

  removeMarkerFromFeatures(markerId: string) {
    // WIP
    // Problem: marker gets hidden but reappears
    filterNames.forEach(filterName => {
      const filterData = this.markerToFeatures(
        this.props.markers.filter(marker => marker.markerId !== markerId),
        filterName
      )
      if (this.map) {
        this.map.getSource(filterName + 'Source').setData(filterData)
      }
    })
  }

  updateFilters(updatedFilter: string, visibility: string) {
    // Show / hide targeted layer
    // (Filtering happens through React state update)
    if (this.map) {
      this.map.setLayoutProperty(
        updatedFilter + 'Layer',
        'visibility',
        visibility
      )
    }
  }

  markerToFeatures(markers: Marker[], filter = '') {
    const featureList: Feature[] = []

    // Hide disabled markers
    markers = markers.filter(
      marker =>
        !Object.prototype.hasOwnProperty.call(marker, 'disabled') ||
        marker.disabled === false
    )

    if (this.state.hiddenMarkers && this.state.hiddenMarkers.length > 0) {
      markers = markers.filter(
        marker => !this.state.hiddenMarkers.includes(marker.markerId)
      )
    }

    // if (filter !== '') {
    //   markers = markers.filter(marker => {
    //     // Adapt conditions to current filters:
    //     // A multi-usage spot may appear in another layer
    //     // E.g. a filter1 place where filter4 is possible

    //     const filter1Condition = this.state.filter1On
    //       ? marker.filter1 === false
    //       : true
    //     const filter2Condition = this.state.filter2On
    //       ? marker.filter2 === false
    //       : true
    //     const filter4Condition = this.state.filter4On
    //       ? marker.filter4 === false
    //       : true

    //     if (filter === 'filter1') {
    //       return marker.filter1 === true
    //     } else if (filter === 'filter2') {
    //       return marker.filter2 === true && filter1Condition
    //     } else if (filter === 'filter4') {
    //       return marker.filter4 === true && filter1Condition && filter2Condition
    //     } else if (filter === 'filter3') {
    //       return (
    //         marker.filter3 === true &&
    //         filter4Condition &&
    //         filter1Condition &&
    //         filter2Condition
    //       )
    //     } else {
    //       console.warn('Filter not found:', filter)
    //     }
    //   })
    // }

    markers.map(marker => {
      featureList.push({
        id: marker.markerId,
        geometry: {
          type: 'Point',
          coordinates: [marker.longitude, marker.latitude]
        },
        properties: {
          markerId: marker.markerId,
          title: marker.title,
          description: marker.description,
          nickname: marker.nickname,
          filters: marker.filters,
          longitude: marker.longitude,
          latitude: marker.latitude,
          needsReview: marker.needsReview ? marker.needsReview : false
        }
      })
    })

    return {
      type: 'FeatureCollection',
      features: featureList
    }
  }

  componentDidMount() {
    const map = new mapboxgl.Map({
      container: this.mapContainer,
      style: 'mapbox://styles/ta-interaktiv/cl0cgrlms001e14rtvd7sxxq7',
      zoom: zoom,
      maxZoom: 17,
      minZoom: isMobile ? 3 : 7,
      center: center,
      attributionControl: false
    })

    map.on('load', () => {
      const loadImage = (png: string, name: string) => {
        map.loadImage(png, (error, image) => {
          if (error) {
            throw error
          }
          map.addImage(name, image)
        })
      }

      loadImage(glassIcon, 'filter3')
      loadImage(glassIcon, 'filter1')
      loadImage(glassIcon, 'filter2')
      loadImage(glassIcon, 'filter4')
      loadImage(addPNG, 'add')

      map.addSource('draggableMarkers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      })

      map.addLayer({
        id: 'draggableMarkersLayer',
        type: 'symbol',
        source: 'draggableMarkers',
        layout: {
          'icon-image': 'add',
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
          'icon-size': 0.5
        }
      })

      filterNames.forEach((filterName: string) => {
        map.addSource(filterName + 'Source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        })

        map.addLayer({
          id: filterName + 'Layer',
          type: 'symbol',
          source: filterName + 'Source',
          layout: {
            'icon-image': filterName,
            'icon-allow-overlap': true,
            'icon-size': 0.25
          }
        })

        // console.log(filterName, 'source and layer added to map')
      })

      // Load markers if available (usually: when markers not in cache)
      if (this.needsMarkerOnMapLoad) {
        filterNames.forEach(filterName => {
          const filterData = this.markerToFeatures(
            this.props.markers,
            filterName
          )
          map.getSource(filterName + 'Source').setData(filterData)
        })
      }

      this.props.handleMapLoaded(map)
    })

    map.on('styleimagemissing', function(e) {
      if (debug) {
        console.warn('Missing image:', e.id)
      }
    })

    // change cursor to pointer when user hovers over a clickable feature
    map.on('mouseenter', e => {
      // TODO: make pointer appear
      if (e.features.length) {
        map.getCanvas().style.cursor = 'pointer'
      }
    })

    // reset cursor to default when user is no longer hovering over a clickable feature
    map.on('mouseleave', () => {
      map.getCanvas().style.cursor = ''
    })

    // Warning: these props gets loaded once, then won't get updated anymore.
    // So if userId was created on firebase, its value will be null here.
    const currentLocale = this.props.locale
    const currentUserId = this.props.userId
    const currentArticleId = this.props.articleId

    const validateRequest = this.validateRequest
    const editRequest = this.editRequest
    const deleteRequest = this.deleteRequest
    const suggestChanges = this.suggestChanges

    // Click on feature -> Open popup
    filterNames.forEach(filterName => {
      map.on('click', filterName + 'Layer', (e: MapMouseEvent) => {
        e.preventDefault()
        if (this.popup) {
          this.popup.remove()
        }
        const coordinates = e.features[0].geometry.coordinates.slice()
        const markerprops = e.features[0].properties
        markerprops.filters = JSON.parse(markerprops.filters)

        const container = document.createElement('div')
        const containerId = 'container-' + markerprops.markerId
        container.setAttribute('id', containerId)

        const popup = new mapboxgl.Popup({ focusAfterOpen: false })
          .setLngLat(coordinates)
          .setDOMContent(container)
          .addTo(map)

        ReactDOM.render(
          <PopupContent
            locale={currentLocale}
            marker={markerprops}
            userId={currentUserId}
            articleId={currentArticleId}
            handleValidateRequest={validateRequest}
            handleEditRequest={editRequest}
            handleDeleteRequest={deleteRequest}
            handleSuggestChanges={suggestChanges}
          />,
          document.getElementById(containerId)
        )
        popup._update()
        this.popup = popup
      })
    })

    const onMove = e => {
      this.draggableMarkers[0].geometry.coordinates = [
        e.lngLat.lng,
        e.lngLat.lat
      ]
      if (this.map) {
        this.map.getSource('draggableMarkers').setData({
          type: 'FeatureCollection',
          features: this.draggableMarkers
        })
      }
    }

    const onUp = e => {
      // console.log('New marker position:', e.lngLat)
      this.setState({ newMarkerPos: e.lngLat })
      if (this.map) {
        this.map.off('mousemove', onMove)
        this.map.off('touchmove', onMove)
      }
    }

    map.on('mousedown', 'draggableMarkersLayer', e => {
      e.preventDefault()
      map.getCanvasContainer().style.cursor = 'grab'
      map.on('mousemove', onMove)
      map.once('mouseup', onUp)
    })

    map.on('touchstart', 'draggableMarkersLayer', e => {
      if (e.points.length !== 1) return
      e.preventDefault()
      map.on('touchmove', onMove)
      map.once('touchend', onUp)
    })

    // Click on background -> Add a marker
    map.on('click', (e: MapMouseEvent) => {
      if (this.state.mapEditing) {
        const features = map.queryRenderedFeatures(e.point)

        if (
          !features.length ||
          !Object.prototype.hasOwnProperty.call(
            features[0].properties,
            'markerId'
          )
        ) {
          if (this.state.newMarkerFormVisible === false) {
            this.createDraggableMarker(e.lngLat.lng, e.lngLat.lat)

            this.formVisible = true
            this.setState({
              newMarkerFormVisible: true,
              newMarkerPos: e.lngLat
            })
          }
        }
      }
    })
    this.map = map
  }

  // TODO: use memoization? or: getDerivedStateFromProps?
  // https://fr.reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#what-about-memoization

  componentDidUpdate(previousProps) {
    if (this.props.panTo && this.props.panTo !== previousProps.panTo) {
      if (this.map) {
        this.map.panTo(this.props.panTo)
      }
    }

    if (this.props.markers && this.props.markers.length > 0) {
      filterNames.forEach(filterName => {
        const source = this.map.getSource(filterName + 'Source')
        if (source !== undefined) {
          const filterData = this.markerToFeatures(
            this.props.markers,
            filterName
          )
          source.setData(filterData)
        } else {
          // console.log('Source for', filterName, 'is not loaded yet.')
          this.needsMarkerOnMapLoad = true
        }
      })
    }
  }

  render() {
    return (
      <div>
        {/* <div className='filter-container ui centered grid'>
          <div className='six wide computer twelve wide column'>
            <div className='field'>
              <div className='ui checkbox'>
                <input
                  type='checkbox'
                  name='filter1Filter'
                  id='filter1Filter'
                  onChange={() => {
                    this.updateFilters(
                      'filter1',
                      this.state.filter1On ? 'none' : 'visible'
                    )
                    this.setState({ filter1On: !this.state.filter1On })
                  }}
                  checked={this.state.filter1On}
                />
                <label htmlFor='filter1Filter'>
                  {localisationStrings[this.props.locale].filter1}
                </label>
              </div>
            </div>

            <div className='field'>
              <div className='ui checkbox'>
                <input
                  type='checkbox'
                  name='filter2Filter'
                  id='filter2Filter'
                  onChange={() => {
                    this.updateFilters(
                      'filter2',
                      this.state.filter2On ? 'none' : 'visible'
                    )
                    this.setState({ filter2On: !this.state.filter2On })
                  }}
                  checked={this.state.filter2On}
                />
                <label htmlFor='filter2Filter'>
                  {localisationStrings[this.props.locale].filter2}
                </label>
              </div>
            </div>

            <div className='field'>
              <div className='ui checkbox'>
                <input
                  type='checkbox'
                  name='filter3Filter'
                  id='filter3Filter'
                  onChange={() => {
                    this.updateFilters(
                      'filter3',
                      this.state.filter3On ? 'none' : 'visible'
                    )
                    this.setState({ filter3On: !this.state.filter3On })
                  }}
                  checked={this.state.filter3On}
                />
                <label htmlFor='filter3Filter'>
                  {localisationStrings[this.props.locale].filter3}
                </label>
              </div>
            </div>

            <div className='field'>
              <div className='ui checkbox'>
                <input
                  type='checkbox'
                  name='filter4Filter'
                  id='filter4Filter'
                  onChange={() => {
                    this.updateFilters(
                      'filter4',
                      this.state.filter4On ? 'none' : 'visible'
                    )
                    this.setState({ filter4On: !this.state.filter4On })
                  }}
                  checked={this.state.filter4On}
                />
                <label htmlFor='filter4Filter'>
                  {localisationStrings[this.props.locale].filter4}
                </label>
              </div>
            </div>
          </div>
        </div> */}

        <MapContext.Provider value={this.map}>
          <div
            className={
              'map-container ' +
              (this.state.mapEditing && !this.state.newMarkerFormVisible
                ? 'editing'
                : '')
            }
            ref={el => (this.mapContainer = el)}
          >
            <ActivationButton
              locales={{
                activate: 'Naviguer dans la carte',
                deactivate: 'Quitter la navigation'
              }}
            />
          </div>
        </MapContext.Provider>
        <AddMarkerForm
          locale={this.props.locale}
          visible={this.state.newMarkerFormVisible}
          editMarker={this.state.editMarker}
          adminView={this.props.showAdminView}
          markerData={this.state.markerData}
          articleId={this.props.articleId}
          newMarkerPosition={this.state.newMarkerPos}
          handleClose={this.closeForm}
        />
        <div className='edit-container ui centered grid'>
          <BackButton />
        </div>
      </div>
    )
  }
}

const BackButton = () => {
  const history = useHistory()

  return (
    <button
      onClick={() => {
        history.replace('/list')
      }}
      className='ui white labeled icon button'
    >
      <i className='chevron left icon' /> Quitter l'édition
    </button>
  )
}
